import { useEffect, useRef } from "react";

import { PRESENCE_HEARTBEAT_SECONDS } from "../../config/constants";
import history from "../../config/history";
import { isEnabled } from "../../services/featureFlags";
import { ACTIVE_SESSIONS_FLAG } from "./constants";
import { resolvePageName } from "./pageNames";
import { claimLease, evaluateHeartbeat, jitteredDelayMs, releaseLease, resolveIntervalMs } from "./presenceScheduler";
import { sendSessionHeartbeat } from "./services";

/**
 * Upper bound on the reported path. Nothing this app routes to comes close;
 * the cap is there so a pathological URL cannot turn a background timer into a
 * large request body.
 */
const MAX_PATH_LENGTH = 512;

/**
 * Where the user is, read at beat time.
 *
 * Deliberately a lazy read of the `history` singleton rather than a router
 * hook or a `history.listen` subscription: this component is mounted above the
 * `Switch` so its timer survives navigation, and subscribing would both couple
 * it to the router and re-run the effect on every route change. A beat only
 * needs the current location at the instant it fires, which one read answers.
 *
 * `history.location.pathname` already excludes the query string and hash, so
 * the strip below is redundant today. It stays because that guarantee is one
 * refactor away from being lost, and presence is nowhere near worth leaking a
 * reset token or an email address out of a URL.
 */
const currentPath = () => {
    const pathname = history?.location?.pathname;
    if (typeof pathname !== "string" || !pathname) {
        return "";
    }
    return pathname.split("?")[0].split("#")[0].slice(0, MAX_PATH_LENGTH);
};

/**
 * Renders nothing; emits the presence heartbeat that backs the admin
 * active-sessions page (FCA-6979).
 *
 * Mounted once above the router's `Switch` so the timer survives navigation
 * instead of restarting on every route change. All of the "should we beat?"
 * rules live in `presenceScheduler` — this component only owns the timer, the
 * visibility listener, the current-location read, and the fire-and-forget
 * request.
 *
 * The request is deliberately unobserved: a failed heartbeat is never shown to
 * the user, never retried, and (via the `AUTH_EXCLUDED_PATHS` entry in
 * `authorizationService`) can never trigger a token refresh or a logout.
 * Presence is an observation, not a feature the user depends on.
 */
const SessionHeartbeat = () => {
    const timerRef = useRef(null);
    const tabIdRef = useRef(null);

    if (tabIdRef.current === null) {
        tabIdRef.current = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }

    useEffect(() => {
        const intervalMs = resolveIntervalMs(PRESENCE_HEARTBEAT_SECONDS);
        let cancelled = false;

        const beatOnce = () => {
            const decision = evaluateHeartbeat({
                flagEnabled: isEnabled(ACTIVE_SESSIONS_FLAG),
                isSignedIn: Boolean(localStorage.getItem("userId")),
                visibilityState: typeof document === "undefined" ? "visible" : document.visibilityState,
                tabId: tabIdRef.current,
                now: Date.now(),
                intervalMs,
                storage: window.localStorage
            });

            if (!decision.send) {
                return;
            }

            claimLease({ tabId: tabIdRef.current, now: Date.now(), storage: window.localStorage });
            const path = currentPath();
            sendSessionHeartbeat({ path, page: resolvePageName(path) }).catch(() => {
                /* presence is best-effort; never surface or retry */
            });
        };

        const schedule = () => {
            if (cancelled) {
                return;
            }
            timerRef.current = window.setTimeout(() => {
                beatOnce();
                schedule();
            }, jitteredDelayMs(intervalMs));
        };

        // Beat immediately so a session shows up as soon as it exists rather
        // than up to a full interval later, then settle into the timer.
        beatOnce();
        schedule();

        // Returning to a tab is the strongest possible signal that the user is
        // present, so beat straight away rather than waiting for the next tick.
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                beatOnce();
            }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            cancelled = true;
            if (timerRef.current) {
                window.clearTimeout(timerRef.current);
            }
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            releaseLease({ tabId: tabIdRef.current, storage: window.localStorage });
        };
    }, []);

    return null;
};

export default SessionHeartbeat;
